import React from 'react';
import img01 from '../../images/img01.jpg';
import consultorio01 from '../../images/Gallery/consultorio-01.69dcb74b.JPG';
import consultorio04 from '../../images/Gallery/consultorio-04.5198b973.JPG';
import '../../css/Home.css';
import TituloPagina from '../../js/Components/PageTitle';
import { branches, getOpenStreetMapUrl, getWhatsappUrl } from '../data/branches';

const giganteraBranch = branches.find((branch) => branch.id === 'gigantera') || branches[1];
const mainBranch = branches.find((branch) => branch.id === 'teodomiro') || branches[0];

const heroSlides = [
  {
    badge: 'Nueva sucursal abierta',
    title: 'Ya abrimos en Plaza La Gigantera',
    subtitle: 'Atención dental por cita únicamente en San Pedro Tlaquepaque.',
    description: 'Agenda por WhatsApp o llamada antes de asistir. Te recibimos en nuestra nueva ubicación con valoración, seguimiento y tratamientos dentales profesionales.',
    image: consultorio01,
    primaryLabel: 'Agendar en La Gigantera',
    primaryHref: getWhatsappUrl(giganteraBranch, 'Hola DentalPuente, quiero agendar una cita en Plaza La Gigantera.'),
    secondaryLabel: 'Ver ubicación',
    secondaryHref: getOpenStreetMapUrl(giganteraBranch),
    statOne: 'Ya abrimos',
    statTwo: 'Sólo con cita',
  },
  {
    badge: 'Ortodoncia DentalPuente',
    title: 'Brackets para transformar tu sonrisa',
    subtitle: 'Metálicos o estéticos, con diagnóstico claro y seguimiento profesional.',
    description: 'Te orientamos desde la valoración inicial para elegir el tratamiento adecuado según tu sonrisa, tiempos, hábitos y objetivos.',
    image: img01,
    primaryLabel: 'Solicitar valoración',
    primaryHref: getWhatsappUrl(mainBranch, 'Hola DentalPuente, me gustaría solicitar una valoración para brackets.'),
    secondaryLabel: 'Ver precios',
    secondaryHref: '/Prices',
    statOne: 'Ortodoncia',
    statTwo: 'Plan personalizado',
  },
  {
    badge: 'Dos sucursales',
    title: 'Ahora estamos más cerca de ti',
    subtitle: 'Elige Teodomiro Manzano o Plaza La Gigantera al agendar tu cita.',
    description: 'Centralizamos la información de contacto para que encuentres dirección, WhatsApp, teléfono y mapa de cada sucursal sin confusión.',
    image: consultorio04,
    primaryLabel: 'Ver sucursales',
    primaryHref: '/Contact#sucursales',
    secondaryLabel: 'Enviar WhatsApp',
    secondaryHref: getWhatsappUrl(giganteraBranch),
    statOne: '2 sucursales',
    statTwo: 'Cita previa',
  },
];

const services = [
  {
    icon: '🦷',
    title: 'Ortodoncia',
    text: 'Brackets metálicos o estéticos para alinear tu sonrisa con diagnóstico y seguimiento profesional.',
  },
  {
    icon: '✨',
    title: 'Limpieza dental',
    text: 'Profilaxis, retiro de sarro y cuidado preventivo para mantener dientes y encías saludables.',
  },
  {
    icon: '😁',
    title: 'Estética dental',
    text: 'Opciones enfocadas en mejorar color, forma y armonía de tu sonrisa.',
  },
  {
    icon: '🛡️',
    title: 'Prevención',
    text: 'Revisiones, orientación y tratamientos tempranos para evitar problemas mayores.',
  },
  {
    icon: '👨‍👩‍👧',
    title: 'Atención familiar',
    text: 'Tratamientos para diferentes edades con trato claro, amable y cercano.',
  },
  {
    icon: '📋',
    title: 'Diagnóstico',
    text: 'Valoración clínica para definir el tratamiento adecuado según tus necesidades.',
  },
];

const Home: React.FC = () => {
  const [activeSlide, setActiveSlide] = React.useState(0);
  const [isPlaying, setIsPlaying] = React.useState(true);
  const activeHero = heroSlides[activeSlide];

  const goToSlide = React.useCallback((index: number) => {
    setActiveSlide((index + heroSlides.length) % heroSlides.length);
  }, []);

  const goToNext = React.useCallback(() => {
    setActiveSlide((current) => (current + 1) % heroSlides.length);
  }, []);

  const goToPrevious = React.useCallback(() => {
    setActiveSlide((current) => (current - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  React.useEffect(() => {
    if (!isPlaying) return undefined;
    const timer = window.setInterval(goToNext, 6400);
    return () => window.clearInterval(timer);
  }, [goToNext, isPlaying]);

  return (
    <div>
      <TituloPagina title="DentalPuente | Portada" pageSlug="home" />

      <main className="home-page content-wrap">
        <section className="home-hero-carousel" aria-label="Promociones principales DentalPuente">
          <div className="home-hero-slides">
            {heroSlides.map((slide, index) => (
              <article
                className={`home-hero-slide ${index === activeSlide ? 'active' : ''}`}
                aria-hidden={index !== activeSlide}
                key={slide.title}
              >
                <img src={slide.image} alt="Instalaciones DentalPuente" className="home-hero-slide-image" />
              </article>
            ))}
          </div>

          <div className="home-hero-overlay" />

          <div className="home-hero-content-premium">
            <span className="home-hero-badge">{activeHero.badge}</span>
            <h1>{activeHero.title}</h1>
            <p className="home-hero-subtitle">{activeHero.subtitle}</p>
            <p className="home-hero-description">{activeHero.description}</p>

            <div className="home-hero-actions">
              <a className="dp-btn dp-btn-primary" href={activeHero.primaryHref} target={activeHero.primaryHref.startsWith('http') ? '_blank' : undefined} rel={activeHero.primaryHref.startsWith('http') ? 'noopener noreferrer' : undefined}>
                {activeHero.primaryLabel}
              </a>
              <a className="dp-btn dp-btn-secondary" href={activeHero.secondaryHref} target={activeHero.secondaryHref.startsWith('http') ? '_blank' : undefined} rel={activeHero.secondaryHref.startsWith('http') ? 'noopener noreferrer' : undefined}>
                {activeHero.secondaryLabel}
              </a>
            </div>
          </div>

          <aside className="home-hero-marketing-card" aria-label="Resumen DentalPuente">
            <span>{activeHero.statOne}</span>
            <strong>{activeHero.statTwo}</strong>
            <small>Agenda tu visita y confirma sucursal antes de asistir.</small>
          </aside>

          <div className="home-carousel-controls" aria-label="Controles del carrusel">
            <button type="button" className="home-carousel-arrow" onClick={goToPrevious} aria-label="Slide anterior">‹</button>
            <button type="button" className="home-carousel-play" onClick={() => setIsPlaying((value) => !value)} aria-label={isPlaying ? 'Pausar carrusel' : 'Reproducir carrusel'}>
              {isPlaying ? 'Pausa' : 'Play'}
            </button>
            <button type="button" className="home-carousel-arrow" onClick={goToNext} aria-label="Siguiente slide">›</button>
          </div>

          <div className="home-carousel-dots" aria-label="Seleccionar slide">
            {heroSlides.map((slide, index) => (
              <button
                type="button"
                className={index === activeSlide ? 'active' : ''}
                onClick={() => goToSlide(index)}
                aria-label={`Mostrar: ${slide.title}`}
                aria-current={index === activeSlide ? 'true' : undefined}
                key={slide.title}
              />
            ))}
          </div>
        </section>

        <section className="home-trust-grid" aria-label="Beneficios DentalPuente">
          <article className="home-trust-card">
            <span>01</span>
            <h3>Ética profesional</h3>
            <p>Tratamientos explicados de forma honesta y sin presión innecesaria.</p>
          </article>
          <article className="home-trust-card">
            <span>02</span>
            <h3>Calidad clínica</h3>
            <p>Atención ordenada, limpia y enfocada en resultados funcionales.</p>
          </article>
          <article className="home-trust-card home-trust-card-highlight">
            <span>03</span>
            <h3>Nueva sucursal</h3>
            <p>Plaza La Gigantera ya está atendiendo exclusivamente con cita agendada.</p>
          </article>
        </section>

        <section className="home-mission-section">
          <div className="home-section-header">
            <span className="dp-eyebrow">Nuestra misión</span>
            <h2>Nuestra prioridad es ayudarte a mejorar tu sonrisa</h2>
            <p>
              Otorgar servicios de calidad para el cuidado dental, comprometidos con la ética profesional y enfocados en la satisfacción de nuestros pacientes.
            </p>
          </div>
          <div className="home-mission-panel">
            <div className="home-mission-item">
              <div className="home-mission-icon">✓</div>
              <div>
                <h3>Servicio profesional</h3>
                <p>Mantenemos altos estándares de calidad en cada tratamiento.</p>
              </div>
            </div>
            <div className="home-mission-item">
              <div className="home-mission-icon">♥</div>
              <div>
                <h3>Cuidado humano</h3>
                <p>Te explicamos tus opciones para que tomes decisiones con tranquilidad.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="home-services-section">
          <div className="home-section-header centered">
            <span className="dp-eyebrow">Servicios dentales</span>
            <h2>Tratamientos para cuidar tu sonrisa</h2>
            <p>Servicios esenciales para diagnóstico, prevención, estética y seguimiento dental.</p>
          </div>

          <div className="home-services-grid">
            {services.map((service) => (
              <article className="home-service-card" key={service.title}>
                <div className="home-service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="home-branches-section" id="sucursales">
          <div className="home-section-header centered">
            <span className="dp-eyebrow">Nuestras sucursales</span>
            <h2>Dos ubicaciones para atenderte mejor</h2>
            <p>Elige la sucursal que te quede más cómoda y agenda antes de asistir.</p>
          </div>

          <div className="home-branches-grid">
            {branches.map((branch) => (
              <article className={`home-branch-card ${branch.id === 'gigantera' ? 'home-branch-card-open' : 'home-branch-card-active'}`} key={branch.id}>
                <div className="home-branch-badge">{branch.badge}</div>
                <h3>{branch.name}</h3>
                <p>{branch.address}</p>
                <span>{branch.status}</span>
                <div className="home-branch-actions">
                  <a href={getWhatsappUrl(branch)} target="_blank" rel="noopener noreferrer">WhatsApp</a>
                  <a href={getOpenStreetMapUrl(branch)} target="_blank" rel="noopener noreferrer">Mapa</a>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="home-testimonials-section" aria-label="Reseñas de pacientes DentalPuente">
          <div className="home-section-header centered">
            <span className="dp-eyebrow">Reseñas de pacientes</span>
            <h2>Lo que dicen de DentalPuente</h2>
            <p>Comentarios de pacientes que han compartido su experiencia en Google.</p>
          </div>

          <div className="home-testimonials-grid">
            <article className="home-testimonial-card">
              <div className="home-testimonial-stars" aria-label="5 estrellas">★★★★★</div>
              <p>“Excelente doctores amables te dan tranquilidad y sobre todo conscientes y en el cobro.”</p>
              <div className="home-testimonial-author">
                <span>Vicky Munguía</span>
                <small>Reseña en Google · Hace un año</small>
              </div>
            </article>

            <article className="home-testimonial-card">
              <div className="home-testimonial-stars" aria-label="5 estrellas">★★★★★</div>
              <p>“Las recomiendo ampliamente y su atención es muy amable y nada q lastiman super recomendado.”</p>
              <div className="home-testimonial-author">
                <span>Laura Guadalupe Reyes Garcia</span>
                <small>Reseña en Google · Hace un año</small>
              </div>
            </article>

            <article className="home-testimonial-card">
              <div className="home-testimonial-stars" aria-label="5 estrellas">★★★★★</div>
              <p>“Excelente servicio, personal super capacitado, siempre amables y muy confiables.”</p>
              <div className="home-testimonial-author">
                <span>Francisco Hernandez</span>
                <small>Reseña en Google · Hace 2 años</small>
              </div>
            </article>
          </div>

          <div className="home-testimonials-footer">
            <a
              className="dp-btn dp-btn-secondary"
              href="https://www.google.com/maps/place/DentalPuente/@20.6168106,-103.3776477,17z/data=!4m8!3m7!1s0x8428ad761d9a9471:0x7c75d21610bc4fde!8m2!3d20.6168056!4d-103.3750728!9m1!1b1!16s%2Fg%2F11fxb78gzv"
              target="_blank"
              rel="noopener noreferrer"
            >
              Ver reseñas en Google
            </a>
          </div>
        </section>
        <section className="home-cta-section">
          <div>
            <span className="dp-eyebrow">¿Listo para tu cita?</span>
            <h2>Agenda tu valoración dental</h2>
            <p>Contáctanos por WhatsApp, elige sucursal y resolvemos tus dudas antes de visitarnos.</p>
          </div>
          <a className="dp-btn dp-btn-primary" href={getWhatsappUrl(giganteraBranch)} target="_blank" rel="noopener noreferrer">Solicitar cita</a>
        </section>
      </main>
    </div>
  );
};

export default Home;
